import React, { FC, useState, useEffect, useContext } from 'react';

import { Button, Card, CardContent, Grid, TextField, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';

import IconCardHeader from '../../../sharedModules/layout/IconCardHeader';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';

import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

const UNKNOWN_CONTACT_SX = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(211, 47, 47, 0.1)',
  },
} as const;

interface ContactInfoUpdatingFieldsProps {
  contactId: string;
  firstName: string;
  lastName: string;
  fetchBackflowJobGroup: () => void;
  backflowJobGroupId: string;
}

const ContactInfoUpdatingFields: FC<ContactInfoUpdatingFieldsProps> = (props) => {
  const { contactId, firstName, lastName, fetchBackflowJobGroup, backflowJobGroupId } = props;
  const { showSuccessSnackBar } = useContext(SnackBarContext);
  const [firstNameValue, setFirstNameValue] = useState<string>(firstName || '');
  const [lastNameValue, setLastNameValue] = useState<string>(lastName || '');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setFirstNameValue(firstName || '');
    setLastNameValue(lastName || '');
  }, [firstName, lastName]);

  const handleUpdate = async () => {
    setLoading(true);

    try {
      await axios.put(`/sales_estimates/contacts/${contactId}`, {
        contact: { first_name: firstNameValue, last_name: lastNameValue },
      });
    } catch {
      setErrorMessage('Failed to update contact.');
      setLoading(false);
      return;
    }

    try {
      if (backflowJobGroupId) {
        await axios.post(`/backflows/backflow_job_groups/${backflowJobGroupId}/sync_ordhdr_contact`);
      }
      setErrorMessage('');
      showSuccessSnackBar('Contact updated successfully.');
    } catch {
      setErrorMessage('Contact saved, but failed to update ORDHDR in Servman.');
    } finally {
      fetchBackflowJobGroup();
      setLoading(false);
    }
  };

  return (
    <Card elevation={3}>
      <CardContent>
        <IconCardHeader icon={PersonIcon} title="Contact" />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ErrorMessage message={errorMessage} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Contact First Name"
              value={firstNameValue}
              onChange={(e) => setFirstNameValue(e.target.value)}
              variant="outlined"
              size="small"
              disabled={loading}
              sx={firstNameValue === 'UNKNOWN' ? UNKNOWN_CONTACT_SX : undefined}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Contact Last Name"
              value={lastNameValue}
              onChange={(e) => setLastNameValue(e.target.value)}
              variant="outlined"
              size="small"
              disabled={loading}
              sx={lastNameValue === 'UNKNOWN' ? UNKNOWN_CONTACT_SX : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              Update the values for this app, and also for CONTACT and ORDHDR in Servman.
            </Typography>
          </Grid>
          <Grid item xs={12} textAlign="left">
            <Button variant="contained" style={{ marginTop: 15 }} onClick={handleUpdate}>
              Update
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ContactInfoUpdatingFields;
